import type { FormInstance } from 'antd';

/**
 * Maps a Laravel-style validation error bag ({ field: string[] }) onto an
 * Ant Design Form instance via form.setFields(), so field-level errors from
 * the backend render under the matching Form.Item without each page having
 * to hand-wire validateStatus/help per field.
 *
 * FormInstance<T>'s `setFields` requires field names drawn from T's literal
 * key union, but the error bag's keys come from the backend at runtime and
 * can't be statically checked against any specific form's fields. This is
 * the one narrow, intentional `any`: an escape hatch at the exact seam
 * where compile-time field names meet a runtime-shaped error payload,
 * isolated here instead of forcing an unsound cast at every call site.
 */
export const setAntdFormErrors = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: FormInstance<any>,
  errors: Record<string, string[]>,
): void => {
  form.setFields(
    Object.entries(errors).map(([name, messages]) => ({
      name,
      errors: messages,
    })),
  );
};

/**
 * Laravel validation error keys are snake_case (they mirror the request's
 * field/column names); our form field names and payload types are
 * camelCase throughout the frontend. Converts the former to the latter so
 * setAntdFormErrors lands on the right Form.Item — e.g. a backend
 * `current_password` error must map to a `currentPassword` field.
 */
export const snakeToCamelErrorKeys = (errors: Record<string, string[]>): Record<string, string[]> =>
  Object.fromEntries(
    Object.entries(errors).map(([key, messages]) => [
      key.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase()),
      messages,
    ]),
  );
